import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  getCourseWritingQuestions,
  updateWritingQuestions,
  regenerateWritingQuestions,
  CourseWritingQuestion,
} from '../services/courseWritingQuestionService';
import './CourseWritingQuestionsPage.css';


const CourseWritingQuestionsPage: React.FC = () => {
  const { t, i18n } = useTranslation('writing');
  const { courseId } = useParams<{ courseId: string }>();
  useAuth();
  const [questions, setQuestions] = useState<CourseWritingQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [editedGates, setEditedGates] = useState<Record<string, any[]>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState<string | null>(null);
  const [collapsedTranslations, setCollapsedTranslations] = useState<Record<string, boolean>>({});

  const loadQuestions = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getCourseWritingQuestions(courseId);
      setQuestions(data);
      setEditedGates({});
    } catch (err: any) {
      setError(err.message || 'Failed to load quest questions');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleQuestionFieldChange = (
    gateType: string,
    questionIndex: number,
    field: string,
    value: any,
  ) => {
    const gate = questions.find((q) => q.gateType === gateType);
    if (!gate) return;

    const currentQuestions = editedGates[gateType] || [...gate.questions];
    const updated = [...currentQuestions];
    updated[questionIndex] = { ...updated[questionIndex], [field]: value };
    setEditedGates((prev) => ({ ...prev, [gateType]: updated }));
  };

  const handleSave = async (gateType: string) => {
    if (!courseId || !editedGates[gateType]) return;
    setSaving(gateType);
    setSuccessMessage(null);
    try {
      const result = await updateWritingQuestions(courseId, gateType, {
        questions: editedGates[gateType],
      });
      setQuestions((prev) =>
        prev.map((q) => (q.gateType === gateType ? result : q)),
      );
      setEditedGates((prev) => {
        const next = { ...prev };
        delete next[gateType];
        return next;
      });
      setSuccessMessage(t('questionsEditor.savedSuccess', { gate: t(`questionsEditor.gate_${gateType}`) }));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const handleRegenerate = async (gateType: string) => {
    if (!courseId) return;
    setConfirmRegenerate(null);
    setRegenerating(gateType);
    setSuccessMessage(null);
    try {
      const result = await regenerateWritingQuestions(courseId, gateType);
      setQuestions((prev) =>
        prev.map((q) => (q.gateType === gateType ? result : q)),
      );
      setEditedGates((prev) => {
        const next = { ...prev };
        delete next[gateType];
        return next;
      });
      setSuccessMessage(t('questionsEditor.regeneratedSuccess', { gate: t(`questionsEditor.gate_${gateType}`) }));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate');
    } finally {
      setRegenerating(null);
    }
  };

  const toggleTranslation = (key: string) => {
    setCollapsedTranslations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getLocalizedTranslation = (
    gate: CourseWritingQuestion,
    questionIndex: number,
    primaryTranslation: string,
  ): string => {
    const userLang = i18n.language;
    const localeTranslations = gate.translations?.[userLang];
    if (localeTranslations) {
      return localeTranslations[`quest_${questionIndex}.prompt`]
        || localeTranslations[`quest_${questionIndex}.scenario`]
        || localeTranslations[`quest_${questionIndex}.translation`]
        || primaryTranslation;
    }
    return primaryTranslation;
  };

  const renderTranslation = (
    gate: CourseWritingQuestion,
    questionIndex: number,
    primaryValue: string,
  ) => {
    const key = `${gate.gateType}-${questionIndex}`;
    const isOpen = collapsedTranslations[key];
    const displayValue = getLocalizedTranslation(gate, questionIndex, primaryValue);

    return (
      <>
        <button
          type="button"
          className="translation-toggle"
          onClick={() => toggleTranslation(key)}
        >
          <span className={`toggle-arrow ${isOpen ? 'open' : ''}`}>&#9654;</span>
          {t('questionsEditor.translation')}
        </button>
        {isOpen && (
          <div className="question-field">
            <textarea
              value={displayValue}
              onChange={(e) =>
                handleQuestionFieldChange(gate.gateType, questionIndex, 'translation', e.target.value)
              }
            />
          </div>
        )}
      </>
    );
  };

  const renderCombineEditor = (gate: CourseWritingQuestion) => {
    const qs = editedGates[gate.gateType] || gate.questions;
    return qs.map((q: any, i: number) => (
      <div key={q.id || i} className="question-card">
        <div className="question-header">
          <span className="question-number">Q{i + 1}</span>
        </div>
        <div className="question-field-primary">
          <label>{t('questionsEditor.sentences')}</label>
          <textarea
            value={(q.sentences || []).join('\n')}
            onChange={(e) =>
              handleQuestionFieldChange(
                gate.gateType, i, 'sentences',
                e.target.value.split('\n').filter((s: string) => s.trim()),
              )
            }
            rows={3}
          />
        </div>
        <div className="question-field-row">
          <div className="question-field">
            <label>{t('questionsEditor.relationship')}</label>
            <select
              value={q.relationship || 'causal'}
              onChange={(e) =>
                handleQuestionFieldChange(gate.gateType, i, 'relationship', e.target.value)
              }
            >
              <option value="causal">{t('questionsEditor.relCausal')}</option>
              <option value="contrast">{t('questionsEditor.relContrast')}</option>
              <option value="addition">{t('questionsEditor.relAddition')}</option>
              <option value="temporal">{t('questionsEditor.relTemporal')}</option>
              <option value="conditional">{t('questionsEditor.relConditional')}</option>
            </select>
          </div>
          <div className="question-field">
            <label>{t('questionsEditor.suggestedConnectors')}</label>
            <input
              type="text"
              value={(q.suggestedConnectors || []).join(', ')}
              onChange={(e) =>
                handleQuestionFieldChange(
                  gate.gateType, i, 'suggestedConnectors',
                  e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean),
                )
              }
            />
          </div>
        </div>
        {renderTranslation(gate, i, q.translation || '')}
      </div>
    ));
  };

  const renderExpansionEditor = (gate: CourseWritingQuestion) => {
    const qs = editedGates[gate.gateType] || gate.questions;
    return qs.map((q: any, i: number) => (
      <div key={q.id || i} className="question-card">
        <div className="question-header">
          <span className="question-number">Q{i + 1}</span>
        </div>
        <div className="question-field-primary">
          <label>{t('questionsEditor.seedSentence')}</label>
          <input
            type="text"
            value={q.seedSentence || ''}
            onChange={(e) =>
              handleQuestionFieldChange(gate.gateType, i, 'seedSentence', e.target.value)
            }
          />
        </div>
        <div className="question-field-row">
          <div className="question-field">
            <label>{t('questionsEditor.requiredVocabulary')}</label>
            <input
              type="text"
              value={(q.requiredVocabulary || []).join(', ')}
              onChange={(e) =>
                handleQuestionFieldChange(
                  gate.gateType, i, 'requiredVocabulary',
                  e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean),
                )
              }
            />
          </div>
          <div className="question-field">
            <label>{t('questionsEditor.minSentences')}</label>
            <input
              type="number"
              min={1}
              max={10}
              value={q.minSentences || 3}
              onChange={(e) =>
                handleQuestionFieldChange(gate.gateType, i, 'minSentences', parseInt(e.target.value) || 3)
              }
            />
          </div>
        </div>
        {renderTranslation(gate, i, q.translation || '')}
      </div>
    ));
  };

  const renderEssayEditor = (gate: CourseWritingQuestion) => {
    const qs = editedGates[gate.gateType] || gate.questions;
    return qs.map((q: any, i: number) => (
      <div key={q.id || i} className="question-card">
        <div className="question-header">
          <span className="question-number">Q{i + 1}</span>
        </div>
        <div className="question-field-primary">
          <label>{t('questionsEditor.topic')}</label>
          <input
            type="text"
            value={q.topic || ''}
            onChange={(e) =>
              handleQuestionFieldChange(gate.gateType, i, 'topic', e.target.value)
            }
          />
        </div>
        <div className="question-field-primary">
          <label>{t('questionsEditor.instructions')}</label>
          <textarea
            value={q.instructions || ''}
            onChange={(e) =>
              handleQuestionFieldChange(gate.gateType, i, 'instructions', e.target.value)
            }
            rows={3}
          />
        </div>
        <div className="question-field-row">
          <div className="question-field">
            <label>{t('questionsEditor.minWords')}</label>
            <input
              type="number"
              min={50}
              max={500}
              value={q.minWords || 100}
              onChange={(e) =>
                handleQuestionFieldChange(gate.gateType, i, 'minWords', parseInt(e.target.value) || 100)
              }
            />
          </div>
          <div className="question-field">
            <label>{t('questionsEditor.maxWords')}</label>
            <input
              type="number"
              min={50}
              max={500}
              value={q.maxWords || 150}
              onChange={(e) =>
                handleQuestionFieldChange(gate.gateType, i, 'maxWords', parseInt(e.target.value) || 150)
              }
            />
          </div>
        </div>
        <div className="question-field">
          <label>{t('questionsEditor.requiredVocabulary')}</label>
          <input
            type="text"
            value={(q.requiredVocabulary || []).join(', ')}
            onChange={(e) =>
              handleQuestionFieldChange(
                gate.gateType, i, 'requiredVocabulary',
                e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean),
              )
            }
          />
        </div>
        {renderTranslation(gate, i, q.translation || '')}
      </div>
    ));
  };

  const renderGateEditor = (gate: CourseWritingQuestion) => {
    switch (gate.gateType) {
      case 'sentence_combining':
        return renderCombineEditor(gate);
      case 'expansion':
        return renderExpansionEditor(gate);
      case 'essay':
        return renderEssayEditor(gate);
      default:
        return <p>Unknown gate type: {gate.gateType}</p>;
    }
  };

  if (loading) {
    return (
      <div className="quest-questions-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('questionsEditor.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quest-questions-page">
      <div className="page-header">
        <div>
          <h1>{t('questionsEditor.title')}</h1>
          <p className="header-subtitle">
            {t('questionsEditor.subtitle')}
          </p>
        </div>
        <Link to={`/creator/courses/${courseId}`} className="back-btn">
          &#8592; {t('questionsEditor.backToCourses')}
        </Link>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>{t('questionsEditor.dismiss')}</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          <span>{successMessage}</span>
        </div>
      )}

      {questions.length === 0 ? (
        <div className="empty-state">
          <p>{t('questionsEditor.noQuestions')}</p>
          <p>{t('questionsEditor.noQuestionsHint')}</p>
        </div>
      ) : (
        <div className="gates-container">
          {questions.map((gate) => {
            const hasEdits = !!editedGates[gate.gateType];

            return (
              <div
                key={gate.id}
                className={`gate-section${hasEdits ? ' has-unsaved' : ''}`}
              >
                <div className="gate-header">
                  <div className="gate-title">
                    <h2>{t(`questionsEditor.gate_${gate.gateType}`)}</h2>
                    {hasEdits && (
                      <span className="unsaved-badge">{t('questionsEditor.edited')}</span>
                    )}
                    {!hasEdits && gate.isEdited && (
                      <span className="edited-badge">{t('questionsEditor.edited')}</span>
                    )}
                  </div>
                  <p className="gate-description">
                    {t(`questionsEditor.desc_${gate.gateType}`)}
                  </p>
                  <div className="gate-meta">
                    {gate.generatedAt && (
                      <span>
                        {t('questionsEditor.generatedAt')}: {new Date(gate.generatedAt).toLocaleDateString()}
                      </span>
                    )}
                    {gate.metadata?.cefrLevel && (
                      <span>CEFR: {gate.metadata.cefrLevel}</span>
                    )}
                  </div>
                </div>

                <div className="gate-questions">{renderGateEditor(gate)}</div>

                <div className="gate-actions">
                  <div className="gate-actions-primary">
                    <button
                      className="save-btn"
                      disabled={!hasEdits || saving === gate.gateType}
                      onClick={() => handleSave(gate.gateType)}
                    >
                      {saving === gate.gateType ? t('questionsEditor.saving') : t('questionsEditor.saveChanges')}
                    </button>
                  </div>
                  <button
                    className="regenerate-btn"
                    disabled={regenerating === gate.gateType}
                    onClick={() => setConfirmRegenerate(gate.gateType)}
                  >
                    {regenerating === gate.gateType ? t('questionsEditor.regenerating') : t('questionsEditor.regenerateWithAI')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Custom confirm modal for regeneration */}
      {confirmRegenerate && (
        <div className="confirm-overlay" onClick={() => setConfirmRegenerate(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('questionsEditor.regenerateWithAI')}</h3>
            <p>{t('questionsEditor.confirmRegenerate')}</p>
            <div className="confirm-modal-actions">
              <button
                className="confirm-cancel-btn"
                onClick={() => setConfirmRegenerate(null)}
              >
                {t('questionsEditor.dismiss')}
              </button>
              <button
                className="confirm-danger-btn"
                onClick={() => handleRegenerate(confirmRegenerate)}
              >
                {t('questionsEditor.regenerateWithAI')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseWritingQuestionsPage;
